import React, {useState, useEffect} from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Add, Save, Delete } from '@material-ui/icons';
import { replaceSpacesWithUnderscores } from "../../../../lib/utils.es6";
import { toCents, fromCents } from "../../moneyUtils";
import { RatingNodeSelect } from '../shared/allowedRelationships';

var RELATIONSHIP_TYPES = [
  "Principal", "Spouse", "Child", "Parent", "Parent_In_Law",
  "Beneficiary_Adult", "Husband", "Wife", "Daughter", "Son",
  "Mother", "Father", "Mother_In_Law", "Father_In_Law",
  "Uncle", "Aunt", "Niece", "Nephew", "Cousin",
  "Grandparent", "Grandchild", "Step_Father", "Step_Mother",
  "Step_Child", "Fiance",
];


const AddonForm = ({handleAddAddon, initialData, isEditMode, currency, ratingNodes}) => {
  const [addonData, setAddonData] = useState({
    code: "",
    name: "",
    benefit_kind: "money",
    relationship: [],
    granularity: "Year",
    cardinality: "",
    benefit_amount: "",
    waiting_period_cardinality: "",
    waiting_period_granularity: "Day",
    premium_amount: "",
    premium_granularity: "Month",
    premium_cardinality: "",
    max_count: {},
    cover_rating_node_guid: "",
    premium_rating_node_guid: "",
    waiting_period_rating_node_guid: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [newRelType, setNewRelType] = useState("");
  const [newRelCount, setNewRelCount] = useState("");

  useEffect(() => {
    if (isEditMode && initialData) {
      // Migration shim: waiting_period_days → cardinality/granularity
      var waitingCardinality = "";
      var waitingGranularity = "Day";
      if (initialData.waiting_period_cardinality !== undefined && initialData.waiting_period_cardinality !== null) {
        waitingCardinality = initialData.waiting_period_cardinality !== 0 ? String(initialData.waiting_period_cardinality) : "";
        waitingGranularity = initialData.waiting_period_granularity || "Day";
      } else if (initialData.waiting_period_days !== undefined && initialData.waiting_period_days !== null) {
        waitingCardinality = initialData.waiting_period_days !== 0 ? String(initialData.waiting_period_days) : "";
        waitingGranularity = "Day";
      }

      var mc = initialData.max_count || {};
      var mcMapped = {};
      RELATIONSHIP_TYPES.forEach(function(rel) {
        mcMapped[rel] = mc[rel] !== undefined && mc[rel] !== null ? String(mc[rel]) : "";
      });
      setAddonData({
        code: initialData.code || "",
        name: initialData.name || "",
        benefit_kind: initialData.benefit_kind || "money",
        relationship: initialData.relationship || [],
        granularity: initialData.granularity || "Year",
        cardinality: initialData.cardinality || "",
        benefit_amount: fromCents(initialData.benefit_amount_in_cents),
        waiting_period_cardinality: waitingCardinality,
        waiting_period_granularity: waitingGranularity,
        premium_amount: fromCents(initialData.premium_amount_in_cents),
        premium_granularity: initialData.premium_granularity || "Month",
        premium_cardinality: initialData.premium_cardinality || "",
        max_count: mcMapped,
        cover_rating_node_guid: initialData.cover_rating_node_guid || "",
        premium_rating_node_guid: initialData.premium_rating_node_guid || "",
        waiting_period_rating_node_guid: initialData.waiting_period_rating_node_guid || "",
      });
    }
  }, [isEditMode, initialData]);

  const validateForm = () => {
    const newErrors = {};

    if (!addonData.name.trim()) {
      newErrors.name = "Add-on name is required";
    } else if (addonData.name.trim().length < 2) {
      newErrors.name = "Add-on name must be at least 2 characters";
    }

    if (!addonData.relationship || addonData.relationship.length === 0) {
      newErrors.relationship = "At least one eligible relationship must be selected";
    }

    if (!addonData.cardinality || addonData.cardinality === "") {
      newErrors.cardinality = "Cardinality is required";
    } else if (parseInt(addonData.cardinality) <= 0) {
      newErrors.cardinality = "Cardinality must be greater than 0";
    }

    if (!addonData.benefit_amount && addonData.benefit_amount !== 0) {
      newErrors.benefit_amount = "Benefit amount is required";
    } else if (Number(addonData.benefit_amount) < 0) {
      newErrors.benefit_amount = "Benefit amount cannot be negative";
    }

    if (!addonData.premium_amount && addonData.premium_amount !== 0) {
      newErrors.premium_amount = "Premium amount is required";
    } else if (Number(addonData.premium_amount) < 0) {
      newErrors.premium_amount = "Premium amount cannot be negative";
    }

    if (!addonData.premium_cardinality || addonData.premium_cardinality === "") {
      newErrors.premium_cardinality = "Premium cardinality is required";
    } else if (parseInt(addonData.premium_cardinality) <= 0) {
      newErrors.premium_cardinality = "Premium cardinality must be greater than 0";
    }

    if (addonData.waiting_period_cardinality === "" || addonData.waiting_period_cardinality === null || addonData.waiting_period_cardinality === undefined) {
      newErrors.waiting_period_cardinality = "Waiting period is required";
    } else if (parseInt(addonData.waiting_period_cardinality) < 0) {
      newErrors.waiting_period_cardinality = "Waiting period cannot be negative";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getFieldError = (fieldName) => {
    return touched[fieldName] && errors[fieldName] ? errors[fieldName] : "";
  };

  const hasFieldError = (fieldName) => {
    return touched[fieldName] && Boolean(errors[fieldName]);
  };

  const handleAddonInputChange = (field) => (event) => {
    setTouched(prev => ({ ...prev, [field]: true }));

    if (field === "name") {
      const generatedCode = replaceSpacesWithUnderscores(event.target.value.toLowerCase().trim());
      setAddonData({
        ...addonData,
        name: event.target.value,
        code: generatedCode,
      });
      return;
    }

    let value = event.target.value;

    if (event.target.type === 'number') {
      if (value === "") {
        value = "";
      } else {
        const numValue = parseFloat(value);
        value = isNaN(numValue) ? "" : numValue;
      }
    }

    setAddonData({
      ...addonData,
      [field]: value,
    });

    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleMaxCountChange = (rel) => (event) => {
    var value = event.target.value;
    setAddonData({
      ...addonData,
      max_count: { ...addonData.max_count, [rel]: value },
    });
  };

  const handleAddRelationship = () => {
    if (!newRelType) return;
    var updated = [...addonData.relationship, newRelType];
    setAddonData({
      ...addonData,
      relationship: updated,
      max_count: { ...addonData.max_count, [newRelType]: newRelCount },
    });
    setTouched(prev => ({ ...prev, relationship: true }));
    setNewRelType("");
    setNewRelCount("");
    if (errors.relationship) {
      setErrors(prev => { var ne = { ...prev }; delete ne.relationship; return ne; });
    }
  };

  const handleRemoveRelationship = (rel) => {
    var updated = addonData.relationship.filter(function(r) { return r !== rel; });
    setAddonData({ ...addonData, relationship: updated, max_count: { ...addonData.max_count, [rel]: "" } });
    setTouched(prev => ({ ...prev, relationship: true }));
  };

  const handleAddAddonSubmit = (event) => {
    event.preventDefault();

    const allFields = ['name', 'relationship', 'cardinality', 'benefit_amount', 'premium_amount', 'premium_cardinality', 'waiting_period_cardinality'];
    const touchedState = {};
    allFields.forEach(field => {
      touchedState[field] = true;
    });
    setTouched(touchedState);

    if (!validateForm()) {
      return;
    }

    var addonMaxCount = {};
    addonData.relationship.forEach(function(rel) {
      addonMaxCount[rel] = parseInt(addonData.max_count[rel]) || 1;
    });

    const submissionData = {
      code: addonData.code,
      name: addonData.name,
      benefit_kind: addonData.benefit_kind,
      relationship: addonData.relationship,
      granularity: addonData.granularity,
      cardinality: addonData.cardinality === "" ? 1 : parseInt(addonData.cardinality) || 1,
      benefit_amount_in_cents: toCents(addonData.benefit_amount),
      waiting_period_cardinality: addonData.waiting_period_cardinality === "" ? 0 : parseInt(addonData.waiting_period_cardinality) || 0,
      waiting_period_granularity: addonData.waiting_period_granularity || "Day",
      premium_amount_in_cents: toCents(addonData.premium_amount),
      premium_granularity: addonData.premium_granularity,
      premium_cardinality: addonData.premium_cardinality === "" ? 1 : parseInt(addonData.premium_cardinality) || 1,
      max_count: addonMaxCount,
      cover_rating_node_guid: addonData.cover_rating_node_guid || null,
      premium_rating_node_guid: addonData.premium_rating_node_guid || null,
      waiting_period_rating_node_guid: addonData.waiting_period_rating_node_guid || null,
    };

    handleAddAddon(submissionData);
  };

  const moneyInputProps = currency ? {
    startAdornment: (
      <InputAdornment position="start">
        <Typography variant="body2" sx={{ color: "text.secondary" }}>{currency}</Typography>
      </InputAdornment>
    )
  } : {};

  return (
    <>
      <Box>
        {/* Add-on Code and Name Row */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mb: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Add-on Name
            </Typography>
            <TextField
              fullWidth
              placeholder="Emergency Roadside Assistance"
              value={addonData.name}
              onChange={handleAddonInputChange("name")}
              variant="outlined"
              error={hasFieldError("name")}
              helperText={getFieldError("name")}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Add-on Code
            </Typography>
            <TextField
              fullWidth
              placeholder="emergency_assist"
              value={addonData.code}
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
          </Box>
        </Box>

        {/* Benefit Kind */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Benefit Kind
          </Typography>
          <FormControl fullWidth variant="outlined">
            <Select
              value={addonData.benefit_kind}
              onChange={handleAddonInputChange("benefit_kind")}
            >
              <MenuItem value="money">Money</MenuItem>
              <MenuItem value="quantity">Quantity</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Eligible Relationships */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Eligible Relationships
          </Typography>

          {/* Selected relationships */}
          {addonData.relationship.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {addonData.relationship.map(function(rel) {
                return (
                  <Box
                    key={rel}
                    sx={{
                      display: "grid", gridTemplateColumns: "1fr 1fr auto",
                      gap: 2, mb: 1, p: 1.5,
                      backgroundColor: "#f9fafb", borderRadius: 1,
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{rel}</Typography>
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Max Count"
                      value={addonData.max_count[rel] || ""}
                      onChange={handleMaxCountChange(rel)}
                    />
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <IconButton
                        size="small"
                        onClick={function() { handleRemoveRelationship(rel); }}
                        sx={{ color: "#ef4444" }}
                      >
                        <Delete sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Add relationship row */}
          {RELATIONSHIP_TYPES.filter(function(r) { return !addonData.relationship.includes(r); }).length > 0 && (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 2 }}>
              <FormControl fullWidth size="small">
                <Select
                  value={newRelType}
                  onChange={function(e) { setNewRelType(e.target.value); }}
                  displayEmpty
                >
                  <MenuItem value="" disabled>Select relationship</MenuItem>
                  {RELATIONSHIP_TYPES.filter(function(r) { return !addonData.relationship.includes(r); }).map(function(r) {
                    return <MenuItem key={r} value={r}>{r}</MenuItem>;
                  })}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Max count"
                value={newRelCount}
                onChange={function(e) { setNewRelCount(e.target.value); }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={handleAddRelationship}
                sx={{
                  textTransform: "none",
                  borderColor: "#173E4B",
                  color: "#173E4B",
                  "&:hover": { backgroundColor: "#f0f9ff" },
                  whiteSpace: "nowrap",
                }}
              >
                Add
              </Button>
            </Box>
          )}

          {getFieldError("relationship") && (
            <FormHelperText error sx={{ mt: 1 }}>
              {getFieldError("relationship")}
            </FormHelperText>
          )}
        </Box>

        {/* Benefit Amount, Granularity, and Cardinality Row */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 3, mb: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Benefit Amount{currency ? ' (' + currency + ')' : ''}
            </Typography>
            <TextField
              fullWidth
              type="number"
              placeholder="0.00"
              value={addonData.benefit_amount}
              onChange={handleAddonInputChange("benefit_amount")}
              variant="outlined"
              error={hasFieldError("benefit_amount")}
              helperText={getFieldError("benefit_amount")}
              InputProps={moneyInputProps}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Granularity
            </Typography>
            <FormControl fullWidth variant="outlined">
              <Select
                value={addonData.granularity}
                onChange={handleAddonInputChange("granularity")}
              >
                <MenuItem value="Day">Day</MenuItem>
                <MenuItem value="Week">Week</MenuItem>
                <MenuItem value="Month">Month</MenuItem>
                <MenuItem value="Year">Year</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Cardinality
            </Typography>
            <TextField
              fullWidth
              type="number"
              placeholder="1"
              value={addonData.cardinality}
              onChange={handleAddonInputChange("cardinality")}
              variant="outlined"
              error={hasFieldError("cardinality")}
              helperText={getFieldError("cardinality")}
            />
          </Box>
        </Box>

        {/* Premium Amount, Premium Granularity, and Premium Cardinality Row */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 3, mb: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Premium Amount{currency ? ' (' + currency + ')' : ''}
            </Typography>
            <TextField
              fullWidth
              type="number"
              placeholder="0.00"
              value={addonData.premium_amount}
              onChange={handleAddonInputChange("premium_amount")}
              variant="outlined"
              error={hasFieldError("premium_amount")}
              helperText={getFieldError("premium_amount")}
              InputProps={moneyInputProps}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Premium Granularity
            </Typography>
            <FormControl fullWidth variant="outlined">
              <Select
                value={addonData.premium_granularity}
                onChange={handleAddonInputChange("premium_granularity")}
              >
                <MenuItem value="Day">Day</MenuItem>
                <MenuItem value="Week">Week</MenuItem>
                <MenuItem value="Month">Month</MenuItem>
                <MenuItem value="Year">Year</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Premium Cardinality
            </Typography>
            <TextField
              fullWidth
              type="number"
              placeholder="1"
              value={addonData.premium_cardinality}
              onChange={handleAddonInputChange("premium_cardinality")}
              variant="outlined"
              error={hasFieldError("premium_cardinality")}
              helperText={getFieldError("premium_cardinality")}
            />
          </Box>
        </Box>

        {/* Waiting Period */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
            Waiting Period
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Cardinality</Typography>
              <TextField
                fullWidth
                type="number"
                placeholder="0"
                value={addonData.waiting_period_cardinality}
                onChange={handleAddonInputChange("waiting_period_cardinality")}
                variant="outlined"
                error={hasFieldError("waiting_period_cardinality")}
                helperText={getFieldError("waiting_period_cardinality")}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Granularity</Typography>
              <FormControl fullWidth variant="outlined">
                <Select
                  value={addonData.waiting_period_granularity}
                  onChange={handleAddonInputChange("waiting_period_granularity")}
                >
                  <MenuItem value="Day">Day</MenuItem>
                  <MenuItem value="Week">Week</MenuItem>
                  <MenuItem value="Month">Month</MenuItem>
                  <MenuItem value="Year">Year</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>

        {/* Rating Node References */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
            Rating Node References (optional)
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
            <RatingNodeSelect
              value={addonData.cover_rating_node_guid}
              onChange={handleAddonInputChange("cover_rating_node_guid")}
              ratingNodes={ratingNodes}
              nodeTypes={['flat_rate', 'multi_factor_rating']}
              appliesTo="cover"
              packageCode={null}
              placeholder="Cover Node"
            />
            <RatingNodeSelect
              value={addonData.premium_rating_node_guid}
              onChange={handleAddonInputChange("premium_rating_node_guid")}
              ratingNodes={ratingNodes}
              nodeTypes={['flat_rate', 'multi_factor_rating']}
              appliesTo="premium"
              packageCode={null}
              placeholder="Premium Node"
            />
            <RatingNodeSelect
              value={addonData.waiting_period_rating_node_guid}
              onChange={handleAddonInputChange("waiting_period_rating_node_guid")}
              ratingNodes={ratingNodes}
              nodeTypes={['waiting_period_flat', 'waiting_period_rating']}
              appliesTo="waiting_period"
              packageCode={null}
              placeholder="Waiting Period Node"
            />
          </Box>
        </Box>

        {/* Submit Button */}
        <Button
          fullWidth
          variant="contained"
          startIcon={isEditMode ? <Save /> : <Add />}
          sx={{
            textTransform: "none",
            backgroundColor: "#fba92c",
            "&:hover": {
              backgroundColor: "#f59e0b",
            },
            py: 1.5,
          }}
          onClick={handleAddAddonSubmit}
        >
          {isEditMode ? "Save Changes" : "Add Add-on"}
        </Button>
      </Box>
    </>
  );
};

export default AddonForm;
